"use client";

import { useState } from "react";
import { Box, Typography } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import { getValidImageUrl, fallback_image } from "@/utils/imageUtils";

/**
 * Individual article preview card (client component for image error handling)
 */
export default function ArticlePreviewCard({ article, isPriority = false }) {
  const articleTitle = article?.title || "Article Title";
  const articleCategory =
    article?.category?.name || article?.categorySlug || "News";
  const categorySlug = article?.categorySlug || "news";
  const articleSlug = article?.slug || "article";
  const articleExcerpt = article?.metaDescription || article?.excerpt || "";

  // State for image with fallback on error
  const [imageSrc, setImageSrc] = useState(
    getValidImageUrl(article?.image) || fallback_image,
  );

  // Get first 1-2 sentences for excerpt
  const truncatedExcerpt =
    articleExcerpt.length > 120
      ? articleExcerpt.slice(0, 120) + "..."
      : articleExcerpt;

  const articleUrl = `/blog/${categorySlug}/${articleSlug}`;

  return (
    <Link
      href={articleUrl}
      style={{ textDecoration: "none", display: "block", height: "100%" }}
    >
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderRadius: "16px",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "0 4px 20px rgba(60, 86, 59, 0.1)",
            transform: "translateY(-2px)",
            "& .article-title": {
              color: "primary.main",
            },
            "& .read-more": {
              textDecoration: "underline",
            },
          },
        }}
      >
        {/* Thumbnail */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "180px",
            overflow: "hidden",
          }}
        >
          <Image
            src={imageSrc}
            alt={articleTitle}
            fill
            priority={isPriority}
            style={{ objectFit: "cover" }}
            sizes="(max-width: 768px) 100vw, 350px"
            onError={() => setImageSrc(fallback_image)}
          />
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Category */}
          <Typography
            sx={{
              color: "primary.main",
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              mb: 1,
            }}
          >
            {articleCategory}
          </Typography>

          {/* Title */}
          <Typography
            className="article-title"
            variant="h3"
            component="h3"
            sx={{
              color: "text.primary",
              fontWeight: 600,
              mb: 1.5,
              lineHeight: 1.3,
              fontSize: "1.1rem",
              transition: "color 0.2s ease",
            }}
          >
            {articleTitle}
          </Typography>

          {/* Excerpt */}
          {truncatedExcerpt && (
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.9rem",
                lineHeight: 1.6,
                mb: 2,
                flex: 1,
              }}
            >
              {truncatedExcerpt}
            </Typography>
          )}

          {/* Read More */}
          <Typography
            className="read-more"
            sx={{
              color: "primary.main",
              fontWeight: 600,
              fontSize: "0.9rem",
            }}
          >
            Read More →
          </Typography>
        </Box>
      </Box>
    </Link>
  );
}
